import React, { useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import {
  FUNDING_RIGHT_DRAWER_CLASS,
  getFundGeniusPortalAttrs,
} from './fundingDrawerUi';
import { useTheme } from '../../../context/ThemeContext';
import { emptyManualIntakeForm } from '../../../utils/fundingClientIntakeUtils';
import { formatDraftSourceLabel } from '../../../services/funding/fundingClientDraftsService';
import {
  revealClientApplicationPassword,
  revealClientEin,
  revealClientSsn,
} from '../../../services/funding/fundingClientsService';
import { copyTextToClipboard, toastCopied, toastCopyFailed } from '../../../utils/fundingClipboardUtils';

const SecureEditField = ({
  value,
  onChange,
  placeholder,
  id,
  showCopy = false,
  existingMasked = '',
  hasExisting = false,
  existingHelperPrefix = 'Existing value saved',
  onReveal,
  revealErrorMessage = 'Unable to reveal value',
  existingTestId,
}) => {
  const [visible, setVisible] = useState(false);
  const [revealedPreview, setRevealedPreview] = useState('');
  const [busy, setBusy] = useState(false);

  const handleCopy = async (copyValue) => {
    if (!copyValue?.trim()) return;
    try {
      await copyTextToClipboard(copyValue);
      toastCopied();
    } catch {
      toastCopyFailed();
    }
  };

  const handleReveal = async () => {
    if (value?.trim()) {
      setVisible((current) => !current);
      return;
    }
    if (!hasExisting || !onReveal) return;
    setBusy(true);
    try {
      const next = await onReveal();
      if (!next) {
        toastCopyFailed(revealErrorMessage);
        return;
      }
      setRevealedPreview(next);
      setVisible(true);
    } catch (error) {
      toastCopyFailed(error?.message || revealErrorMessage);
    } finally {
      setBusy(false);
    }
  };

  const copyTarget = value?.trim() || (visible ? revealedPreview : '');

  return (
    <div>
      {hasExisting && existingMasked ? (
        <p className="mb-2 text-xs text-[var(--fg-muted)]" data-testid={existingTestId}>
          {existingHelperPrefix}: {existingMasked}
        </p>
      ) : null}
      <div className="fg-secure-input-row">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
        />
        {hasExisting || value?.trim() ? (
          <button
            type="button"
            className="fg-inline-icon-btn"
            onClick={handleReveal}
            disabled={busy}
            aria-label={visible ? 'Hide value' : 'Reveal value'}
            data-testid={id ? `${id}-reveal` : undefined}
          >
            {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        ) : null}
        {showCopy ? (
          <button
            type="button"
            className="fg-inline-icon-btn"
            onClick={() => handleCopy(copyTarget)}
            disabled={!copyTarget?.trim()}
            aria-label="Copy"
            data-testid={id ? `${id}-copy` : undefined}
          >
            <Copy className="size-4" />
          </button>
        ) : null}
      </div>
      {visible && revealedPreview && !value?.trim() ? (
        <p className="mt-2 text-xs font-mono text-[var(--fg-text)]" data-testid={id ? `${id}-revealed-preview` : undefined}>
          {revealedPreview}
        </p>
      ) : null}
    </div>
  );
};

const SecureInput = ({ value, onChange, placeholder, id, showCopy = false }) => (
  <SecureEditField
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    id={id}
    showCopy={showCopy}
  />
);

const CopyableInput = ({ value, onChange, placeholder, id, type = 'text', testId }) => {
  const handleCopy = async () => {
    if (!value?.trim()) return;
    try {
      await copyTextToClipboard(value);
      toastCopied();
    } catch {
      toastCopyFailed();
    }
  };

  return (
    <div className="fg-secure-input-row">
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
      />
      <button
        type="button"
        className="fg-inline-icon-btn"
        onClick={handleCopy}
        disabled={!value?.trim()}
        aria-label="Copy"
        data-testid={testId ? `${testId}-copy` : undefined}
      >
        <Copy className="size-4" />
      </button>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={`funding-client-field fg-form-field ${full ? 'full' : ''}`}>
    <label>{label}</label>
    {children}
  </div>
);

const Section = ({ num, title, desc, children, testId }) => (
  <div className="funding-client-form-section fg-form-section" data-testid={testId}>
    <div className="funding-client-form-section__heading fg-section-title">
      <span className="funding-client-form-section__number">{num}</span>
      <div>
        <h4 className="funding-client-form-section__title">{title}</h4>
        <p className="funding-client-form-section__subtitle">{desc}</p>
      </div>
    </div>
    <div className="funding-client-form-section__grid fg-form-grid">{children}</div>
  </div>
);

const STATUS_OPTIONS = ['Draft', 'Documents Pending', 'Ready for Analysis', 'Submitted', 'Funded'];
const ENTITY_OPTIONS = ['', 'LLC', 'Sole Proprietor', 'Corporation', 'S-Corp', 'Partnership', 'Nonprofit'];
const PRODUCT_OPTIONS = ['', '0% Business Credit Card', 'Business Line of Credit', 'Personal Funding', 'Business Loan', 'SBA', 'Equipment Financing', 'Real Estate Funding'];
const STAGE_OPTIONS = ['', '1. Intake', '2. Readiness', '3. Documents', '4. Match', '5. Sequence', '6. Submit', '7. Funded'];
const APPLICATION_TYPE_OPTIONS = ['', 'Business PG', 'Business No PG', 'Personal', 'Full-doc', 'Low-doc', 'No-doc'];

const ManualFundingClientDrawer = ({
  open,
  onOpenChange,
  onSubmit,
  onSaveDraft,
  initialValues,
  onOpenPaste,
  drafts = [],
  onResumeDraft,
  onDeleteDraft,
  showSampleBanner = false,
  mode = 'create',
  clientId = '',
  agencyId = '',
  submitting = false,
  sensitiveReentryNotice = '',
}) => {
  const isEditMode = mode === 'edit';
  const [form, setForm] = useState(() => emptyManualIntakeForm());

  const revealExistingSsn = () => (clientId && agencyId ? revealClientSsn(clientId, agencyId) : Promise.resolve(''));
  const revealExistingEin = () => (clientId && agencyId ? revealClientEin(clientId, agencyId) : Promise.resolve(''));
  const revealExistingPassword = () => (
    clientId && agencyId ? revealClientApplicationPassword(clientId, agencyId) : Promise.resolve('')
  );

  React.useEffect(() => {
    if (open) {
      setForm(initialValues || emptyManualIntakeForm());
    }
  }, [open, initialValues]);

  const { resolvedMode } = useTheme();
  const portalAttrs = getFundGeniusPortalAttrs(resolvedMode);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    onSubmit?.(form);
  };

  const handleSaveDraft = () => {
    onSaveDraft?.(form);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className={`${FUNDING_RIGHT_DRAWER_CLASS} fg-manual-drawer w-full overflow-y-auto sm:max-w-2xl`}
        data-testid="manual-funding-client-drawer"
        {...portalAttrs}
      >
        <SheetHeader>
          <SheetTitle>{isEditMode ? 'Edit Funding Client' : 'Manual Funding Client'}</SheetTitle>
          <SheetDescription>
            {isEditMode
              ? 'Update the client profile, identity, business, and funding details.'
              : 'Enter the full client, identity, business, income, address, credit, and funding profile manually.'}
          </SheetDescription>
        </SheetHeader>

        {showSampleBanner ? (
          <div className="fg-hint-box mt-3" data-testid="manual-intake-sample-banner">
            Sample data. Not a real client.
          </div>
        ) : null}

        {sensitiveReentryNotice ? (
          <div className="fg-hint-box mt-3" data-testid="manual-intake-sensitive-reentry">
            {sensitiveReentryNotice}
          </div>
        ) : null}

        {!isEditMode && drafts.length ? (
          <div className="mt-4 rounded-xl border border-[var(--fg-line)] p-4" data-testid="manual-intake-resume-drafts">
            <h4 className="text-sm font-semibold text-[var(--fg-text)]">Resume Draft</h4>
            <p className="mt-1 text-xs text-[var(--fg-muted)]">Pick up where you left off on a saved client draft.</p>
            <ul className="mt-3 space-y-2">
              {drafts.map((draft) => (
                <li
                  key={draft.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[var(--fg-surface-muted)] px-3 py-2"
                  data-testid={`manual-intake-draft-${draft.id}`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-[var(--fg-text)]">{draft.name}</div>
                    <div className="text-xs text-[var(--fg-muted)]">
                      Updated {new Date(draft.updatedAt).toLocaleString()} · Source: {formatDraftSourceLabel(draft.source)}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      className="fg-btn fg-btn-soft"
                      data-testid={`manual-intake-resume-${draft.id}`}
                      onClick={() => onResumeDraft?.(draft)}
                    >
                      Resume
                    </button>
                    <button
                      type="button"
                      className="fg-btn fg-btn-light"
                      data-testid={`manual-intake-delete-draft-${draft.id}`}
                      onClick={() => onDeleteDraft?.(draft.id)}
                    >
                      Delete draft
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!isEditMode && onOpenPaste ? (
          <div className="mt-3">
            <button
              type="button"
              className="fg-text-btn"
              data-testid="manual-open-paste-prefill-btn"
              onClick={onOpenPaste}
            >
              Have copied client info? Paste and prefill
            </button>
          </div>
        ) : null}

        <form
          onSubmit={handleSubmit}
          noValidate
          className="funding-client-drawer__body fg-manual-intake mt-4 space-y-5 pb-28"
          data-testid="manual-intake-form"
        >
          <Section num="1" title="Client Contact" desc="Basic client contact information." testId="manual-section-contact">
            <Field label="First Name"><input value={form.firstName} onChange={set('firstName')} placeholder="First name" /></Field>
            <Field label="Middle Name"><input value={form.middleName} onChange={set('middleName')} placeholder="Middle name" data-testid="manual-middle-name" /></Field>
            <Field label="Last Name"><input value={form.lastName} onChange={set('lastName')} placeholder="Last name" /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={set('email')} placeholder="client@example.com" /></Field>
            <Field label="Phone"><input value={form.phone} onChange={set('phone')} placeholder="+1 555 555 5555" /></Field>
            <Field label="Status">
              <select value={form.status} onChange={set('status')}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Source"><input value={form.source} onChange={set('source')} placeholder="Manual intake" /></Field>
          </Section>

          <Section num="2" title="Identity Profile" desc="Personal identity fields used for lender applications." testId="manual-section-identity">
            <Field label="Ownership %"><input value={form.ownership} onChange={set('ownership')} placeholder="100%" /></Field>
            <Field label="SSN">
              <SecureEditField
                id="manual-ssn"
                value={form.ssn}
                onChange={set('ssn')}
                placeholder={isEditMode && form.hasClientSsn ? 'Leave blank to keep existing SSN' : 'Enter SSN'}
                existingMasked={form.ssnMasked}
                hasExisting={isEditMode && form.hasClientSsn}
                existingHelperPrefix="Existing SSN saved"
                existingTestId="manual-existing-ssn"
                onReveal={revealExistingSsn}
                revealErrorMessage="Unable to reveal SSN"
              />
            </Field>
            <Field label="DOB"><input value={form.dob} onChange={set('dob')} placeholder="MM/DD/YYYY" /></Field>
            <Field label="Personal Address" full><input value={form.personalAddress} onChange={set('personalAddress')} placeholder="Street, city, state" /></Field>
            <Field label="Time Live in House"><input value={form.timeLiveInHouse} onChange={set('timeLiveInHouse')} placeholder="Years and months" /></Field>
            <Field label="Housing Payment"><input value={form.housingPayment} onChange={set('housingPayment')} placeholder="Monthly payment" /></Field>
          </Section>

          <Section
            num="3"
            title="Application Access"
            desc="Credentials created for lender/application submissions."
            testId="manual-section-application-access"
          >
            <Field label="Application Email Created">
              <CopyableInput
                id="manual-application-email-input"
                testId="manual-application-email"
                type="email"
                value={form.applicationEmailCreated}
                onChange={set('applicationEmailCreated')}
                placeholder="Application login email"
              />
            </Field>
            <Field label="Application Password Created" full>
              <div data-testid="manual-application-password">
                <SecureEditField
                  id="manual-application-password-input"
                  value={form.applicationPasswordCreated}
                  onChange={set('applicationPasswordCreated')}
                  placeholder={isEditMode ? 'Leave blank to keep existing password' : 'Application login password'}
                  existingMasked={form.hasExistingApplicationPassword ? '••••••••' : ''}
                  hasExisting={isEditMode && form.hasExistingApplicationPassword}
                  existingHelperPrefix="Existing password saved"
                  existingTestId="manual-existing-application-password"
                  onReveal={revealExistingPassword}
                  revealErrorMessage="Unable to reveal password"
                  showCopy
                />
              </div>
            </Field>
            <p className="full text-xs text-[var(--fg-muted)]" style={{ gridColumn: '1 / -1' }}>
              Credentials created for lender/application submissions.
            </p>
          </Section>

          <Section num="4" title="Business Profile" desc="Business identity and operating information." testId="manual-section-business">
            <Field label="Business Legal Name" full><input value={form.businessLegalName} onChange={set('businessLegalName')} placeholder="Business legal name" /></Field>
            <Field label="Full EIN">
              <SecureEditField
                id="manual-ein"
                value={form.ein}
                onChange={set('ein')}
                placeholder={isEditMode && form.hasClientEin ? 'Leave blank to keep existing EIN' : 'Enter EIN'}
                existingMasked={form.einMasked}
                hasExisting={isEditMode && form.hasClientEin}
                existingHelperPrefix="Existing EIN saved"
                existingTestId="manual-existing-ein"
                onReveal={revealExistingEin}
                revealErrorMessage="Unable to reveal EIN"
              />
            </Field>
            <Field label="Business Start Date"><input value={form.businessStartDate} onChange={set('businessStartDate')} placeholder="MM/DD/YYYY" /></Field>
            <Field label="Business Address" full><input value={form.businessAddress} onChange={set('businessAddress')} placeholder="Street, city, state" /></Field>
            <Field label="Business Phone"><input value={form.businessPhone} onChange={set('businessPhone')} placeholder="Business phone" /></Field>
            <Field label="Business Email"><input type="email" value={form.businessEmail} onChange={set('businessEmail')} placeholder="Business email" /></Field>
            <Field label="Industry"><input value={form.industry} onChange={set('industry')} placeholder="Industry" /></Field>
            <Field label="Entity Type">
              <select value={form.entityType} onChange={set('entityType')}>
                {ENTITY_OPTIONS.map((t) => (
                  <option key={t || 'empty'} value={t}>{t || 'Select entity type'}</option>
                ))}
              </select>
            </Field>
          </Section>

          <Section num="5" title="Income & Employment" desc="Income, employment, and capacity data." testId="manual-section-income">
            <Field label="Personal Income"><input value={form.personalIncome} onChange={set('personalIncome')} placeholder="Annual personal income" /></Field>
            <Field label="Business Income"><input value={form.businessIncome} onChange={set('businessIncome')} placeholder="Annual business income" /></Field>
            <Field label="Employer"><input value={form.employer} onChange={set('employer')} placeholder="Employer or self-employed" /></Field>
            <Field label="Time Working At"><input value={form.timeWorkingAt} onChange={set('timeWorkingAt')} placeholder="Years and months" /></Field>
            <Field label="Monthly Revenue"><input value={form.monthlyRevenue} onChange={set('monthlyRevenue')} placeholder="Monthly revenue" /></Field>
            <Field label="Monthly Deposits"><input value={form.monthlyDeposits} onChange={set('monthlyDeposits')} placeholder="Monthly deposits" /></Field>
            <Field label="Business Expenses"><input value={form.businessExpenses} onChange={set('businessExpenses')} placeholder="Monthly expenses" /></Field>
            <Field label="Savings"><input value={form.savings} onChange={set('savings')} placeholder="Savings balance" /></Field>
            <Field label="Retirement Account"><input value={form.retirement} onChange={set('retirement')} placeholder="Retirement balance" /></Field>
            <Field label="Bank Name"><input value={form.bankName} onChange={set('bankName')} placeholder="Primary bank" /></Field>
          </Section>

          <Section num="6" title="Credit Profile" desc="Scores and approval positioning data." testId="manual-section-credit">
            <Field label="Experian Score"><input value={form.experian} onChange={set('experian')} placeholder="300–850" /></Field>
            <Field label="TransUnion Score"><input value={form.transUnion} onChange={set('transUnion')} placeholder="300–850" /></Field>
            <Field label="Equifax Score"><input value={form.equifax} onChange={set('equifax')} placeholder="300–850" /></Field>
            <Field label="Current Highest Limit"><input value={form.highestLimit} onChange={set('highestLimit')} placeholder="Credit limit" /></Field>
            <Field label="Open Inquiries"><input value={form.inquiries} onChange={set('inquiries')} placeholder="Inquiry count" /></Field>
            <Field label="Utilization %"><input value={form.utilization} onChange={set('utilization')} placeholder="Utilization %" /></Field>
            <Field label="Derogatory Items"><input value={form.derogatoryItems} onChange={set('derogatoryItems')} placeholder="Count" /></Field>
            <Field label="Readiness %"><input value={form.readiness} onChange={set('readiness')} placeholder="Readiness %" /></Field>
          </Section>

          <Section num="7" title="Funding Goal" desc="Funding amount, product target, and application strategy." testId="manual-section-funding">
            <Field label="Desired Funding Amount"><input value={form.desiredFunding} onChange={set('desiredFunding')} placeholder="Target funding amount" /></Field>
            <Field label="Target Product">
              <select value={form.targetProduct} onChange={set('targetProduct')}>
                {PRODUCT_OPTIONS.map((p) => (
                  <option key={p || 'empty'} value={p}>{p || 'Select product'}</option>
                ))}
              </select>
            </Field>
            <Field label="Funding Stage">
              <select value={form.fundingStage} onChange={set('fundingStage')}>
                {STAGE_OPTIONS.map((s) => (
                  <option key={s || 'empty'} value={s}>{s || 'Select stage'}</option>
                ))}
              </select>
            </Field>
            <Field label="Application Type">
              <select value={form.applicationType} onChange={set('applicationType')}>
                {APPLICATION_TYPE_OPTIONS.map((t) => (
                  <option key={t || 'empty'} value={t}>{t || 'Select application type'}</option>
                ))}
              </select>
            </Field>
            <Field label="Personal Funding Amount"><input value={form.personalFundingAmount} onChange={set('personalFundingAmount')} placeholder="Amount" /></Field>
            <Field label="0% Credit Card Amount"><input value={form.zeroPercentAmount} onChange={set('zeroPercentAmount')} placeholder="Amount" /></Field>
            <Field label="Business LOC Amount"><input value={form.businessLocAmount} onChange={set('businessLocAmount')} placeholder="Amount" /></Field>
            <Field label="Target Lender"><input value={form.targetLender} onChange={set('targetLender')} placeholder="Target lenders" /></Field>
          </Section>

          <Section num="8" title="Notes & Tags" desc="Internal advisor notes and segmentation." testId="manual-section-notes">
            <Field label="Tags"><input value={form.tags} onChange={set('tags')} placeholder="Comma-separated tags" /></Field>
            <Field label="Advisor Owner"><input value={form.advisorOwner} onChange={set('advisorOwner')} placeholder="Assigned advisor" /></Field>
            <Field label="Notes" full><textarea value={form.notes} onChange={set('notes')} rows={4} placeholder="Internal notes" /></Field>
          </Section>

          <div className="funding-client-drawer__footer fg-drawer-footer">
            <button
              type="button"
              className="fg-btn fg-btn-light"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            {!isEditMode ? (
              <button
                type="button"
                className="fg-btn fg-btn-soft"
                data-testid="manual-save-draft-btn"
                onClick={handleSaveDraft}
                disabled={submitting}
              >
                Save Draft
              </button>
            ) : null}
            <button
              type="submit"
              className="fg-btn fg-btn-primary"
              data-testid={isEditMode ? 'manual-save-client-submit' : 'manual-create-client-submit'}
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting
                ? (isEditMode ? 'Saving…' : 'Creating…')
                : (isEditMode ? 'Save' : 'Create Client')}
            </button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default ManualFundingClientDrawer;
